import React, { useState } from "react";
import {
  Collapse,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTranslation } from "../i18n/useTranslation";

const refreshOptions = [10, 20, 30, 60, 120];
const offlineOptions = [1, 3, 6, 12, 24, 48, 72];

const SettingsPanel = ({
  mapLayer,
  onMapLayerChange,
  drawLines,
  onDrawLinesChange,
  language,
  onLanguageChange,
  refreshIntervalSeconds,
  onRefreshIntervalChange,
  offlineThresholdHours,
  onOfflineThresholdChange,
  hideOffline,
  onHideOfflineChange,
  hideTrainProgress,
  onHideTrainProgressChange,
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const toggleExpanded = () => setExpanded((prev) => !prev);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 2,
        border: "1px solid rgba(15,23,42,0.08)",
        bgcolor: "rgba(15,23,42,0.02)",
      }}
    >
      <Stack spacing={2}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {t("settings.title")}
          </Typography>
          <IconButton
            size="small"
            onClick={toggleExpanded}
            aria-label={expanded ? t("settings.collapse") : t("settings.expand")}
          >
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Stack>

        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Stack spacing={3}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2.5}
              alignItems={{ md: "flex-start" }}
            >
              <Stack spacing={1} sx={{ flex: 1, width: "100%" }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: 0.6,
                  }}
                >
                  {t("settings.language")}
                </Typography>
                <ToggleButtonGroup
                  exclusive
                  value={language}
                  onChange={(_, value) => value && onLanguageChange(value)}
                  size="small"
                  sx={{
                    display: "flex",
                    width: "100%",
                    maxWidth: 260,
                    gap: 1,
                    px: 0.5,
                    py: 0.5,
                    bgcolor: "rgba(15,23,42,0.04)",
                    borderRadius: "20px",
                    "& .MuiToggleButtonGroup-grouped": {
                      border: 0,
                      borderRadius: "999px !important",
                      margin: 0,
                    },
                    "& .MuiToggleButton-root": {
                      textTransform: "none",
                      fontWeight: 600,
                      letterSpacing: 0.1,
                      flex: 1,
                      borderRadius: "16px !important",
                      bgcolor: "transparent",
                      border: "1px solid transparent",
                      transition: "all 0.2s ease",
                      "&.Mui-selected": {
                        bgcolor: "#ffffff",
                        borderColor: "rgba(15,23,42,0.12)",
                        boxShadow: "0 4px 10px rgba(15,23,42,0.08)",
                      },
                      "&:hover": {
                        bgcolor: "rgba(15,23,42,0.08)",
                      },
                    },
                  }}
                >
                  <ToggleButton value="ar">{t("settings.language.ar")}</ToggleButton>
                  <ToggleButton value="en">{t("settings.language.en")}</ToggleButton>
                </ToggleButtonGroup>
              </Stack>

              <Stack spacing={1} sx={{ flex: 1, width: "100%" }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: 0.6,
                  }}
                >
                  {t("settings.mapLayer")}
                </Typography>
                <ToggleButtonGroup
                  exclusive
                  value={mapLayer}
                  onChange={(_, value) => value && onMapLayerChange(value)}
                  size="small"
                  sx={{
                    display: "flex",
                    width: "100%",
                    maxWidth: 280,
                    gap: 1,
                    px: 0.5,
                    py: 0.5,
                    bgcolor: "rgba(15,23,42,0.04)",
                    borderRadius: "20px",
                    "& .MuiToggleButtonGroup-grouped": {
                      border: 0,
                      borderRadius: "999px !important",
                      margin: 0,
                    },
                    "& .MuiToggleButton-root": {
                      textTransform: "none",
                      fontWeight: 600,
                      letterSpacing: 0.1,
                      flex: 1,
                      borderRadius: "16px !important",
                      bgcolor: "transparent",
                      border: "1px solid transparent",
                      transition: "all 0.2s ease",
                      "&.Mui-selected": {
                        bgcolor: "#ffffff",
                        borderColor: "rgba(15,23,42,0.12)",
                        boxShadow: "0 4px 10px rgba(15,23,42,0.08)",
                      },
                      "&:hover": {
                        bgcolor: "rgba(15,23,42,0.08)",
                      },
                    },
                  }}
                >
                  <ToggleButton value="transport">{t("settings.layer.transport")}</ToggleButton>
                  <ToggleButton value="standard">{t("settings.layer.standard")}</ToggleButton>
                </ToggleButtonGroup>
              </Stack>
            </Stack>

            <Stack direction={{ xs: "column", md: "row" }} spacing={2.5}>
              <FormControl fullWidth>
                <InputLabel>{t("settings.refresh")}</InputLabel>
                <Select
                  label={t("settings.refresh")}
                  value={refreshIntervalSeconds}
                  onChange={(event) => onRefreshIntervalChange(Number(event.target.value))}
                >
                  {refreshOptions.map((value) => (
                    <MenuItem key={value} value={value}>
                      {value === 1
                        ? t("settings.refreshSecond", { value })
                        : t("settings.refreshSeconds", { value })}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>{t("settings.offlineThreshold")}</InputLabel>
                <Select
                  label={t("settings.offlineThreshold")}
                  value={offlineThresholdHours}
                  onChange={(event) => onOfflineThresholdChange(Number(event.target.value))}
                >
                  {offlineOptions.map((value) => (
                    <MenuItem key={value} value={value}>
                      {value === 1
                        ? t("settings.offlineHour", { value })
                        : t("settings.offlineHours", { value })}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>

            <Stack spacing={1.5}>
              <FormControlLabel
                control={
                  <Switch
                    checked={drawLines}
                    onChange={(event) => onDrawLinesChange(event.target.checked)}
                    color="primary"
                  />
                }
                label={t("settings.drawLines")}
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={hideOffline}
                    onChange={(event) => onHideOfflineChange(event.target.checked)}
                    color="primary"
                  />
                }
                label={t("settings.hideOffline")}
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={hideTrainProgress}
                    onChange={(event) =>
                      onHideTrainProgressChange(event.target.checked)
                    }
                    color="primary"
                  />
                }
                label={t("settings.hideTrainProgress")}
              />
            </Stack>
          </Stack>
        </Collapse>
      </Stack>
    </Paper>
  );
};

export default SettingsPanel;
